import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { SW } from "@/i18n/sw";
import { relocalize, startDomI18n } from "@/i18n/dom";

const KEY = "vacs-lang";
const LANGS = ["en", "sw"];
let current = LANGS.includes(localStorage.getItem(KEY)) ? localStorage.getItem(KEY) : "en";

// Keys are the English source text: a missing Kiswahili entry falls back to English, never to a raw key.
export const tr = (text, vars) => {
  const s = (current === "sw" && SW[text]) || text;
  return vars ? s.replace(/\{(\w+)\}/g, (m, k) => (vars[k] ?? m)) : s;
};
export const getLang = () => current;
export const locale = () => (current === "sw" ? "sw-TZ" : "en-GB");

const Ctx = createContext({ lang: "en", setLang: () => {}, t: tr });

export function LangProvider({ children }) {
  const { user, setUser } = useAuth();
  const [lang, setL] = useState(current);
  const apply = (l) => { current = l; localStorage.setItem(KEY, l); document.documentElement.lang = l; setL(l); relocalize(); };
  useEffect(() => { if (user && LANGS.includes(user.language) && user.language !== current) apply(user.language); }, [user?.id, user?.language]); // eslint-disable-line
  useEffect(() => { document.documentElement.lang = current; startDomI18n(() => current); }, []);
  const setLang = useCallback((l) => {
    apply(l);
    if (user) api.put("/auth/me", { language: l }).then((r) => setUser((u) => ({ ...u, language: r.data.language }))).catch(() => {});
  }, [user, setUser]);
  const t = useCallback((text, vars) => tr(text, vars), [lang]); // eslint-disable-line
  return <Ctx.Provider value={{ lang, setLang, t }}>{children}</Ctx.Provider>;
}

export const useLang = () => useContext(Ctx);

export function LanguageSwitch({ className = "" }) {
  const { lang, setLang } = useLang();
  return (
    <div role="group" aria-label="Language / Lugha" className={`inline-flex overflow-hidden rounded-[10px] border bg-card text-xs font-semibold ${className}`} data-testid="lang-switch">
      {[["en", "EN", "English"], ["sw", "SW", "Kiswahili"]].map(([k, l, name]) => (
        <button key={k} type="button" onClick={() => lang !== k && setLang(k)} aria-pressed={lang === k} aria-label={name} title={name}
          className={`px-2.5 py-2 transition-colors ${lang === k ? "bg-[#00A9D7] text-black" : "text-muted-foreground hover:bg-muted"}`} data-testid={`lang-${k}`}>{l}</button>
      ))}
    </div>
  );
}
